/**
 * Emailed links carry a one-time secret as `?token=`. The pages that take one
 * move it out of the URL on arrival, but the arrival request itself is traced
 * on the server, so everything telemetry sends is scrubbed as well. Only the
 * value is hidden; the parameter's presence still tells a reader what the
 * request was.
 */
export function redactSecretQuery(text: string): string {
  return text.replace(/([?&]token=)[^&#\s]*/g, "$1[redacted]");
}

type Breadcrumb = { data?: Record<string, unknown> };
type Span = { description?: string; data?: Record<string, unknown> };

/** The parts of a Sentry event or transaction that can name a URL. */
export interface UrlBearingEvent {
  transaction?: string;
  request?: { url?: string; query_string?: unknown };
  breadcrumbs?: Breadcrumb[];
  spans?: Span[];
}

const URL_KEYS = ["url", "from", "to", "http.url", "http.query"];

function scrubData(data: Record<string, unknown> | undefined) {
  if (!data) return data;
  const scrubbed = { ...data };
  for (const key of URL_KEYS) {
    const value = scrubbed[key];
    if (typeof value === "string") scrubbed[key] = redactSecretQuery(value);
  }
  return scrubbed;
}

export function scrubSecretQuery<E extends UrlBearingEvent>(event: E): E {
  const scrubbed = { ...event };
  if (typeof scrubbed.transaction === "string") {
    scrubbed.transaction = redactSecretQuery(scrubbed.transaction);
  }
  if (scrubbed.request) {
    const request = { ...scrubbed.request };
    if (typeof request.url === "string")
      request.url = redactSecretQuery(request.url);
    if (typeof request.query_string === "string") {
      request.query_string = redactSecretQuery(
        `?${request.query_string}`
      ).slice(1);
    }
    scrubbed.request = request;
  }
  if (scrubbed.breadcrumbs) {
    scrubbed.breadcrumbs = scrubbed.breadcrumbs.map((crumb) => ({
      ...crumb,
      data: scrubData(crumb.data),
    }));
  }
  if (scrubbed.spans) {
    scrubbed.spans = scrubbed.spans.map((span) => ({
      ...span,
      description:
        typeof span.description === "string"
          ? redactSecretQuery(span.description)
          : span.description,
      data: scrubData(span.data),
    }));
  }
  return scrubbed;
}
